import { Button } from "@/components/ui/button";
import { User, HelpCircle, LogOut, LayoutDashboard } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import wellsuitesLogo from "@/assets/wellsuites-logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ProviderHeader = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleSupportClick = () => {
    navigate("/support");
  };

  const userName = user?.user_metadata?.first_name && user?.user_metadata?.last_name
    ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
    : user?.email || "Provider";
  
  const userEmail = user?.email || "";

  return (
    <header className="bg-background border-b border-border sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <Link to="/management">
              <img 
                src={wellsuitesLogo}
                alt="Wellsuites" 
                className="h-5 w-auto"
              />
            </Link>
          </div>
          
          <div className="flex items-center space-x-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-10 w-10 rounded-full p-0 hover:bg-foreground/5">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user?.user_metadata?.avatar_url} alt={userName} />
                      <AvatarFallback className="bg-foreground text-background font-semibold text-sm">
                        {userName.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 bg-card shadow-xl border border-border rounded-xl" align="end">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="font-semibold text-foreground">{userName}</p>
                    <p className="text-sm text-muted-foreground">{userEmail}</p>
                  </div>
                  
                  <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3">
                    <Link to="/management" className="w-full flex items-center">
                      <LayoutDashboard className="h-4 w-4 mr-3 text-muted-foreground" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3">
                    <Link to="/profile" className="w-full flex items-center">
                      <User className="h-4 w-4 mr-3 text-muted-foreground" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem 
                    className="hover:bg-muted cursor-pointer py-3"
                    onClick={handleSupportClick}
                  >
                    <HelpCircle className="h-4 w-4 mr-3 text-muted-foreground" />
                    Support
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    className="hover:bg-muted cursor-pointer py-3"
                    onClick={handleSignOut}
                  >
                    <LogOut className="h-4 w-4 mr-3 text-muted-foreground" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button asChild variant="ghost" className="font-semibold text-foreground hover:bg-foreground/5 rounded-full">
                  <Link to="/auth">Log in</Link>
                </Button>
                <Button asChild className="rounded-full font-semibold">
                  <Link to="/auth">Sign up</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default ProviderHeader;